import { Maybe } from "@graphql-types";
import { lazy } from "react";
import { css } from "styled-components";

export const BUTTON_HEIGHT = 46;
export const HEADER_HEIGHT = 84;

// screen sizes
export const SMALLER_MOBILE = 400;
export const MOBILE_BREAKPOINT = 600;
export const IPAD_BREAKPOINT = 768;
export const TABLET_BREAKPOINT = 1000;
export const IPAD_PRO_BREAKPOINT = 1025;
export const SMALL_LAPTOP_BREAKPOINT = 1280;
export const LAPTOP_BREAKPOINT = 1441;
export const TWENTY_INCH_SCREEN = 1600;
export const IMAC_BREAKPOINT = 1921;
export const DEFAULT_OFFSET = 120;
export const MOBILE_OFFSET = 60;

export const colors = {
  transparent: "transparent",
  red: "#D6001C",
  white: "#fff",
  black: "#000",
  flightBlue: "#24468E",
  primary: "#1B1B1C",
  grey: "#666666",
  lightGrey: "#F1F3F5",
  charcoal: "#1B1B1C",
  gold: "#EFCE2D",
  lightGray: "#C0BFBF",
  bgColor: "#848484",
  lightGray2: "#797979",
  darkPrink: "#e4007e",
};

/** Outline / ghost buttons on light backgrounds (#1B1B1C at 10% opacity) */
export const buttonOutlineBorder = "rgba(27, 27, 28, 0.1)";
/** Outline buttons on dark backgrounds (white at 10% opacity) */
export const buttonOutlineBorderOnDark = "rgba(255, 255, 255, 0.1)";

export const fontWeights = {
  theme: 300,
  normal: 400,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

export const PRIMARY_COLOR = colors.primary;
export const SECONDARY_COLOR = colors.white;
export const LIGHTGREY_COLOR = colors.lightGray;
export const GREYBG_COLOR = colors.lightGray;
export const BLACK_COLOR = colors.black;
export const PINK_COLOR = colors.darkPrink;
export const FONT_BODY = `neue-haas-grotesk-display, sans-serif, Arial`;
export const FONT_PARAGRAPH = `neue-haas-grotesk-display, sans-serif, Arial`;
export const FONT_BODY_WEIGHT = fontWeights.theme;
export const FONT_HEADER = `PP Formula, sans-serif, Arial`;
export const FONT_HEADER_WEIGHT = fontWeights.theme;
export const Eurostile_BQ = `Eurostile BQ`;
export const INTER_FONT = `Inter`;
export const FONT_LINK = colors.lightGray2;
export const BOORSOK_FONT = `boorsok, sans-serif, Arial`;

export const fontDefault = 18;

export const fontSizes = {
  p: { default: fontDefault, mobile: 16 },
  h1: { default: 70, mobile: 42 },
  h2: { default: 55, mobile: 32 },
  h3: { default: 45, mobile: 26 },
  h4: { default: 22, mobile: 22 },
  span: { default: fontDefault, mobile: 16 },
};

export const colorsRGB = {
  flightBlue: (opacity?: number) => `rgba(0, 28, 168, ${opacity ?? "0.4"})`,
  black: (opacity?: number) => `rgba(0, 0, 0, ${opacity ?? "0.2"})`,
  white: (opacity?: number) => `rgba(255, 255, 255, ${opacity ?? "0.3"})`,
  charcoal: (opacity?: number) => `rgba(17, 17, 46, ${opacity ?? "0.3"})`,
  red: (opacity?: number) => `rgba(214, 0, 28, ${opacity ?? "0.3"})`,
};

export const pages = {
  home: "/",
  aboutUs: "/about",
  contactUs: "/contact-us",
  search: "/search",
  notFound: "/404",
};

/** True when Sanity `slug.current` is the site home (root) slug. */
export function isSanitySlugHome(slug: string | null | undefined): boolean {
  if (slug == null) return false;
  const s = String(slug).trim();
  return s === "" || s === "/" || s === pages.home;
}

/** True when Sanity `slug.current` is the About page (hero shows play / controls). */
export function isSanitySlugAbout(slug: string | null | undefined): boolean {
  if (slug == null) return false;
  const raw = String(slug).trim();
  const s = raw.toLowerCase();
  if (s === "about" || s === "/about") return true;
  if (s === "about-us" || s.endsWith("/about-us")) return true;
  const norm = (raw.startsWith("/") ? raw : `/${raw}`).replace(/\/+/g, "/");
  const low = norm.toLowerCase();
  if (norm === pages.aboutUs || low === "/about" || low.endsWith("/about")) return true;
  const segments = low.split("/").filter(Boolean);
  return segments[segments.length - 1] === "about";
}

// define paths to asset
export const assets = {
  logo: require("@static/assets/logo.png").default,
};

export const buttonStyle = {
  base: {
    bg: `${colors.transparent}`,
    text: `${colors.flightBlue}`,
    border: buttonOutlineBorder,
    hoverBg: `${colors.flightBlue}`,
    hoverBorder: `${colors.flightBlue}`,
    hoverText: `${colors.white}`,
  },
  white: {
    bg: `${colors.transparent}`,
    text: `${colors.white}`,
    border: buttonOutlineBorderOnDark,
    hoverBg: `${colors.flightBlue}`,
    hoverBorder: `${colors.flightBlue}`,
    hoverText: `${colors.white}`,
  },
  whiteInverse: {
    bg: `${colors.transparent}`,
    text: `${colors.white}`,
    border: buttonOutlineBorderOnDark,
    hoverBg: `${colors.white}`,
    hoverBorder: `${colors.flightBlue}`,
    hoverText: `${colors.flightBlue}`,
  },
};

export const breakPoint = {
  small: MOBILE_BREAKPOINT,
  medium: IPAD_BREAKPOINT,
  large: TABLET_BREAKPOINT,
  wide: LAPTOP_BREAKPOINT,
  mediumLarge: TWENTY_INCH_SCREEN,
};

export const mediaQueryWidths = {
  smallDown: `(max-width: ${breakPoint.small}px)`,
  smallUp: `(min-width: ${breakPoint.small + 1}px)`,
  mediumDown: `(max-width: ${breakPoint.medium}px)`,
  medium: `(min-width: ${breakPoint.medium + 1}px) and (max-width: ${breakPoint.large}px)`,
  mediumUp: `(min-width: ${breakPoint.medium + 1}px)`,
  largeDown: `(max-width: ${breakPoint.large}px)`,
  large: `(min-width: ${breakPoint.large + 1}px) and (max-width: ${breakPoint.wide}px)`,
  largeUp: `(min-width: ${breakPoint.large + 1}px)`,
  wideDown: `(max-width: ${breakPoint.wide}px)`,
  wideUp: `(min-width: ${breakPoint.wide + 1}px)`,
  mediumLarge: `(max-width: ${breakPoint.mediumLarge}px)`,
};

const m = `@media only screen and`;
export const mediaQuery = {
  smallDown: `${m} ${mediaQueryWidths.smallDown}`,
  smallUp: `${m} ${mediaQueryWidths.smallUp}`,
  mediumDown: `${m} ${mediaQueryWidths.mediumDown}`,
  medium: `${m} ${mediaQueryWidths.medium}`,
  mediumUp: `${m} ${mediaQueryWidths.mediumUp}`,
  largeDown: `${m} ${mediaQueryWidths.largeDown}`,
  large: `${m} ${mediaQueryWidths.large}`,
  largeUp: `${m} ${mediaQueryWidths.largeUp}`,
  wideDown: `${m} ${mediaQueryWidths.wideDown}`,
  wideUp: `${m} ${mediaQueryWidths.wideUp}`,
  mediumLarge: `${m} ${mediaQueryWidths.mediumLarge}`,
};

export const space = {
  none: 0,
  tiny: 5,
  xxs: 15,
  xs: 30,
  s: 50,
  m: 75,
  l: 100,
  xl: 130,
  xxl: 175,
  xxxl: 300,
};

export const sectionAttributes = (
  marginBottom: Maybe<string> | undefined,
  key: string | null | undefined,
) => {
  const sectionAttr = {
    className: marginBottom ? `mb-${marginBottom}` : `mb-none`,
    ...(key && { id: `section-id-${key}` }),
  };

  return { sectionAttr };
};

export const marginBottom = {
  none: `margin-bottom: ${space.none}px`,
  tiny: `margin-bottom: ${space.tiny}px`,
  xxs: `margin-bottom: ${space.xxs}px`,
  xs: `margin-bottom: ${space.xs}px`,
  s: `margin-bottom: ${space.s}px`,
  m: `
  margin-bottom: ${space.m}px;
  ${mediaQuery.smallDown}{
    margin-bottom: ${space.xs}px;
  }
  `,
  l: `margin-bottom: ${space.l}px;
  ${mediaQuery.mediumDown}{
    margin-bottom: ${space.s}px;
  }
  `,
  xl: `
  margin-bottom: ${space.xl}px;
  ${mediaQuery.mediumDown}{
    margin-bottom: ${space.s}px;
  }
  `,
  xxl: `
  margin-bottom: ${space.xxl}px;
  ${mediaQuery.mediumDown}{
    margin-bottom: ${space.m}px;
  }
  ${mediaQuery.smallDown}{
    margin-bottom: ${space.s}px;
  }
  `,
};

export const paddingTopBottom = {
  none: `padding-bottom: ${space.none}px;padding-top:${space.none}px;`,
  tiny: `padding-bottom: ${space.tiny}px;padding-top:${space.tiny}px;`,
  xxs: `padding-bottom: ${space.xxs}px;padding-top:${space.xxs}px;`,
  xs: `padding-bottom: ${space.xs}px;padding-top:${space.xs}px;`,
  s: `padding-bottom: ${space.s}px;padding-top:${space.s}px;`,
  m: `padding-bottom: ${space.m}px;padding-top:${space.m}px;`,
  l: `padding-bottom: ${space.l}px;padding-top:${space.l}px;
  ${mediaQuery.mediumDown}{
    padding-bottom: ${space.s}px;
    padding-top: ${space.s}px;
  }
  `,
  xl: `
  padding-bottom: ${space.xl}px;
  padding-top: ${space.xl}px;
  ${mediaQuery.mediumDown}{
    padding-bottom: ${space.s}px;
    padding-top: ${space.s}px;
  }
  `,
  xxl: `
  padding-bottom: ${space.xxl}px;
  padding-top: ${space.xxl}px;
  ${mediaQuery.mediumDown}{
    padding-bottom: ${space.m}px;
    padding-top: ${space.m}px;
  }
  ${mediaQuery.smallDown}{
    padding-bottom: ${space.s}px;
    padding-top: ${space.s}px;
  }
  `,
};
export const containerSideGap = {
  default: 143,
  wide: 100,
  mob: 25,
};

export const containerWidth = {
  default: 1440,
};

/** Matches `FooterMainContainer`: centered content, max 1440px, same horizontal padding per breakpoint */
export const siteContentHorizontalCss = css`
  width: min(100%, ${containerWidth.default}px);
  max-width: ${containerWidth.default}px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
  ${mediaQuery.mediumLarge} {
    padding-left: 64px;
    padding-right: 64px;
  }
  @media only screen and (max-width: 1024px) {
    padding-left: 45px;
    padding-right: 45px;
  }
  ${mediaQuery.smallDown} {
    padding-left: 32px;
    padding-right: 32px;
  }
`;

export const btnSize = {
  l: `
    padding:10px 16px;
    font-size:16px;
  `,
  lWithIcon: `
    padding:10px 16px;
    font-size:16px;
  `,
};

export const btnDesign = {
  default: `
    border-width:1px;
    border-radius:100em;
    border-style:solid;
    text-decoration:none;
    font-size:16px;
    padding:10px 16px;
  `,
  outlineDark: `
    border-color:${buttonOutlineBorder};
    color:${colors.primary};
    background:transparent;
    padding:10px 16px;
    font-size:16px;
  `,
  outlineLight: `
    border-color:${buttonOutlineBorderOnDark};
    color:#fff;
    background:transparent;
    padding:10px 16px;
    font-size:16px;
  `,
  filledLight: `
    border:1px solid white;
    background-color:#ffffff;
    color:black;
    font-size:16px;
    padding:10px 16px;
  `,
  textWhite: `
    color: ${colors.white};
    background: transparent;
    border: none;
    text-decoration: none;
    padding: 0px;
    text-align:left;
    &:hover {
      text-decoration: underline;
    }
  `,
  textBlack: `
    color: ${colors.black};
    background: transparent;
    border: none;
    text-decoration: none;
    padding: 0px;
    text-align:left;
    &:hover {
      text-decoration: underline;
    }
  `,
  textDark: `
    color: ${colors.primary};
    background: transparent;
    border: none;
    text-decoration: none;
    padding: 0px;
    text-decoration: underline;
  `,
  filledDark: `
    border:1px solid black;
    background-color:black;
    color:white;
    font-size:16px;
    padding:10px 16px;
  `,
};

/** Hero + brand/range banner CTAs: `Link` renders `<a><div>…</div></a>` */
export const bannerCtaLinkHover = css`
  a > div {
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  }
  a:hover > div {
    background-color: ${colors.red} !important;
    color: ${colors.white} !important;
    border-color: ${colors.red} !important;
  }
`;

export const gradients = {
  fadeFromTop: `linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)`,
  fadeFromBottom: `linear-gradient(to top, rgba(0, 0, 0, 0.58) 0%, rgba(0, 0, 0, 0.22) 42%, rgba(0, 0, 0, 0) 72%)`,
  primaryFade: `rgba(27, 27, 28, 0.4);`,
  lightOverlay: `rgba(0,0,0, 0.1);`,
};

export const animations = {
  bounce: `  @keyframes bounce {
    0% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-20px);
    }
    50% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(0);
    }
  }
  animation-name: bounce;
  animation-timing-function: ease;
  animation-duration: 2s;
  animation-iteration-count: infinite;`,
  slideInFromLeft: `@keyframes slideInFromLeft {
    0% {
      transform: translateX(-100px);
    }
    100% {
      transform: translateX(0);
    }
  }
    animation: 0.2s ease-out 0s 1 slideInFromLeft;`,
  fadeIn: (seconds: number) => {
    return `
    @keyframes fadein {
      from { opacity: 0; }
      to   { opacity: 1; }
  }
    -webkit-animation: fadein ${seconds ?? 2}s; 
    -moz-animation: fadein ${seconds ?? 2}s;`;
  },
};

export const transitionAll = `
  -webkit-transition: all .25s ease-in-out;
  -moz-transition: all .25s ease-in-out;
  -o-transition: all .25s ease-in-out;
  transition: all .25s ease-in-out;
`;

export const headerSidePadding = 25;

export const urlQueryNames = {
  brand: "brand",
};
export const mapSlugToBrand = {
  "x-shot": "X-Shot",
  "metal-machines": "Metal Machines",
  "max-build-more": "MAX",
  "pets-alive": "Pets Alive",
  "coco-cones": "Coco Cones",
  "mini-brands": "Mini Brands",
  rainbocorns: "Rainbocorns",
  "eggy-wawa": "Eggy Wawa",
  "robo-alive": "Robo Alive",
  "x-shot-water": "XSHOT Water",
  "sparkle-girlz": "Sparkle Girlz",
  babycorns: "Babycorns",
  fuggler: "Fuggler",
  smashers: "Smashers",
  "5-surprise": "5 Surprise",
  "bunch-o-balloons": "Bunch O Balloons",
  fidget: "Fidget Cube",
  xshot: "XShot",
  oosh: "Oosh",
  skins: "Skins",
  "hug-a-lumps": "Hug A Lumps",
  ballers: "Ballers",
  zuru: "ZURU",
};

export const queryTypes = [
  {
    id: 1,
    name: "General Enquiry",
  },
  {
    id: 2,
    name: "Product Feedback/Issue",
  },
  {
    id: 3,
    name: "New Product Idea",
  },
  {
    id: 4,
    name: "Collaboration Request",
  },
  {
    id: 5,
    name: "Wholesale/Distribution Enquiry",
  },
  {
    id: 6,
    name: "Press Enquiry",
  },
];

export const FILE_CONSTRAINTS = {
  MAX_FILES: 3,
  MAX_FILE_SIZE: 20 * 1024 * 1024, // 20MB in bytes
  VALID_TYPES: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "video/mp4",
    "application/pdf",
    "video/mpeg",
    "video/quicktime",
  ],
  VALID_TYPES_TEXT: "JPG, JPEG, PNG, MP4, MPEG, MOV or PDF",
};
export const ERROR_TYPES = {
  INVALID_TYPE: "INVALID_TYPE",
  FILE_TOO_LARGE: "FILE_TOO_LARGE",
  TOO_MANY_FILES: "TOO_MANY_FILES",
};

export const MIME_TYPE_TO_EXTENSION = {
  // Images
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/jpg": "jpg",

  // Video
  "video/mp4": "mp4",
  "video/mpeg": "mpeg",
  "video/quicktime": "mov",

  // Documents
  "application/pdf": "pdf",
};

export const platformsData = [
  {
    id: 1,
    platformName: "Tiktok",
  },
  {
    id: 2,
    platformName: "Instagram",
  },
];
// Default form state values
export const INITIAL_FORM_STATE = {
  firstName: "",
  lastName: "",
  email: "",
  enquiryType: "",
  termsAccepted: false,
  country: "",
  message: "",
  marketingConsent: false,
  isPatentFiled: "",
  patentStatus: "",
  patentURL: "",
};
export const ENQUIRY_TYPE_FIELDS = {
  "Product Feedback/Issue": {
    brand: true,
    product: true,
    store: true,
    feedbackType: true,
    batchCode: true,
    manufacturingCode: true,
  },
  "Collaboration Request": {
    platforms: true,
    combinedFollowing: true,
    diaperSize: true,
  },
  "Wholesale/Distribution Enquiry": {
    company: true,
  },
  "New Product Idea": {
    phoneNumber: true,
    company: true,
    website: true,
    isPatentFiled: true,
    patentStatus: true,
    patentURL: true,
    haveWorkedInToyIndustry: true,
    haveLicensedConcept: true,
    conceptName: true,
    conceptDevelopmentStage: true,
    conceptIndustryScope: true,
    elevatorPitch: true,
    toyIndustries: true,
    isConceptSubmittedToOthers: true,
  },
};
